import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  UserIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { UserService } from '../../../services/super-admin/userService';
import { TenantService } from '../../../services/super-admin/tenantService';
import { PermissionService } from '../../../services/super-admin/permissionService';
import { CreateUserRequest, UpdateUserRequest, SuperAdminUser, PermissionTemplate } from '../../../types/super-admin';

interface UserFormData {
  email: string;
  firstName: string;
  lastName: string;
  tenantId: string;
  role: string;
  permissionTemplateId: string;
  status: 'active' | 'inactive' | 'suspended';
  sendWelcomeEmail: boolean;
}

interface UserFormProps {
  mode: 'create' | 'edit';
}

const UserForm: React.FC<UserFormProps> = ({ mode }) => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Array<{ id: string; name: string }>>([]);
  const [permissionTemplates, setPermissionTemplates] = useState<PermissionTemplate[]>([]);
  const [existingUser, setExistingUser] = useState<SuperAdminUser | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    // setValue,
    watch,
    reset
  } = useForm<UserFormData>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      tenantId: '',
      role: 'user',
      permissionTemplateId: '',
      status: 'active',
      sendWelcomeEmail: true
    }
  });

  const selectedTenantId = watch('tenantId');

  useEffect(() => {
    fetchFormData();
    if (mode === 'edit' && userId) {
      fetchUser();
    }
  }, [mode, userId]);

  const fetchFormData = async () => {
    try {
      // Fetch tenants
      const tenantsResponse = await TenantService.listTenants({ limit: 100 });
      setTenants(tenantsResponse.tenants.map(t => ({ id: t.id, name: t.name })));

      // Fetch permission templates
      const templatesResponse = await PermissionService.listTemplates();
      setPermissionTemplates(templatesResponse.templates);
    } catch (error) {
      console.error('Error fetching form data:', error);
      setError('Failed to load form data');
    }
  };

  const fetchUser = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const user = await UserService.getUser(userId);
      setExistingUser(user);
      
      // Populate form with existing user data
      reset({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        tenantId: user.tenantId,
        role: user.role,
        permissionTemplateId: user.permissionTemplateId || '',
        status: user.status,
        sendWelcomeEmail: false
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    try {
      setError(null);
      setSuccess(null);

      if (mode === 'create') {
        const createData: CreateUserRequest = {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          tenantId: data.tenantId,
          role: data.role,
          permissionTemplateId: data.permissionTemplateId || undefined,
          sendWelcomeEmail: data.sendWelcomeEmail
        };

        await UserService.createUser(createData);
        setSuccess('User created successfully!');
        
        // Redirect after a short delay
        setTimeout(() => {
          navigate('/super-admin/users');
        }, 2000);
      } else {
        const updateData: UpdateUserRequest = {
          firstName: data.firstName,
          lastName: data.lastName,
          role: data.role,
          status: data.status,
          permissionTemplateId: data.permissionTemplateId || undefined,
          tenantId: data.tenantId !== existingUser?.tenantId ? data.tenantId : undefined
        };

        await UserService.updateUser(userId!, updateData);
        setSuccess('User updated successfully!');
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      setError(error.message || 'Failed to save user');
    }
  };

  const handleCancel = () => {
    navigate('/super-admin/users');
  };

  const availableRoles = ['admin', 'editor', 'viewer', 'user'];

  if (loading && mode === 'edit') {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">
          {mode === 'create' ? 'Create User' : 'Edit User'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {mode === 'create' 
            ? 'Add a new user to the system'
            : 'Update user information and permissions'
          }
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">
              {mode === 'create' ? 'User Information' : 'Update User Information'}
            </h3>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {/* Email */}
              <div className="sm:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    disabled={mode === 'edit'}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="user@example.com"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
                {mode === 'edit' && (
                  <p className="mt-1 text-sm text-gray-500">Email cannot be changed after user creation</p>
                )}
              </div>

              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="firstName"
                    {...register('firstName', { required: 'First name is required' })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    placeholder="John"
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  {...register('lastName', { required: 'Last name is required' })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                  placeholder="Doe"
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>

              {/* Tenant */}
              <div>
                <label htmlFor="tenantId" className="block text-sm font-medium text-gray-700">
                  Tenant
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="tenantId"
                    {...register('tenantId', { required: 'Tenant is required' })}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  >
                    <option value="">Select a tenant</option>
                    {tenants.map((tenant) => (
                      <option key={tenant.id} value={tenant.id}>
                        {tenant.name}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.tenantId && (
                  <p className="mt-1 text-sm text-red-600">{errors.tenantId.message}</p>
                )}
                {mode === 'edit' && selectedTenantId !== existingUser?.tenantId && (
                  <p className="mt-1 text-sm text-yellow-600">
                    ⚠️ Changing tenant will transfer this user to a different organization
                  </p>
                )}
              </div>

              {/* Role */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  id="role"
                  {...register('role', { required: 'Role is required' })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                >
                  {availableRoles.map((role) => (
                    <option key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                )}
              </div>

              {/* Permission Template */}
              <div className="sm:col-span-2">
                <label htmlFor="permissionTemplateId" className="block text-sm font-medium text-gray-700">
                  Permission Template (Optional)
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <ShieldCheckIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <select
                    id="permissionTemplateId"
                    {...register('permissionTemplateId')}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  >
                    <option value="">No template (use role-based permissions)</option>
                    {permissionTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.templateType})
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Permission templates override role-based permissions
                </p>
              </div>

              {/* Status (Edit mode only) */}
              {mode === 'edit' && (
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    id="status"
                    {...register('status')}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              )}

              {/* Send Welcome Email (Create mode only) */}
              {mode === 'create' && (
                <div className="sm:col-span-2">
                  <div className="flex items-center">
                    <input
                      id="sendWelcomeEmail"
                      type="checkbox"
                      {...register('sendWelcomeEmail')}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label htmlFor="sendWelcomeEmail" className="ml-2 block text-sm text-gray-900">
                      Send welcome email with login instructions
                    </label>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    User will receive an email with their temporary password and login instructions
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {mode === 'create' ? 'Creating...' : 'Updating...'}
              </>
            ) : (
              mode === 'create' ? 'Create User' : 'Update User'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UserForm;